import { Box, Divider, List, Paper } from '@mui/material';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  LuBookOpenCheck,
  LuBuilding,
  LuCalendar,
  LuFileClock,
  LuFilter,
  LuGlobe,
  LuGraduationCap,
  LuLanguages,
  LuMail,
  LuMap,
  LuNavigation,
  LuPlane,
  LuRefreshCw,
  LuStamp,
} from 'react-icons/lu';
import { TbBriefcase } from 'react-icons/tb';
import { WsColors } from 'wisestep-base';

import CheckboxFilter from '../../../../components/filters/components/CheckboxFilter';
import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import MultiMatchFilter from '../../../../components/filters/components/MultiMatchFilter';
import RadioListFilter from '../../../../components/filters/components/RadioListFilter';
import SalaryRangeFilter from '../../../../components/filters/components/SalaryRangeFilter';
import TextInputFilter from '../../../../components/filters/components/TextInputFilter';
import AdvancedFiltersHeader from '../../../../utils/AdvancedFiltersHeader';
import ExperienceRangeFilter from '../../components/ExperienceRangeFilter';
import BasicSearchForm from '../../types/IBasicSearchForm';
import DiceAdvanceSearch from '../types/IDiceAdvanceSearch';
import {
  additionalFiltersCheckBoxOptions,
  authorizedCountriesCheckBoxOptions,
  compensationOptions,
  compensationTimeOptions,
  educationCheckBoxOptions,
  employmentTypeCheckBoxOptions,
  languagesCheckBoxOptions,
  likelyToMoveOptions,
  travelPreferenceOptions,
  workAuthorizationCheckBoxOptions,
} from '../utils/DiceHelper';

interface AdvanceFilterNewProps {
  performSearch: (values: BasicSearchForm & DiceAdvanceSearch) => void;
}

const AdvanceFilterNew = ({ performSearch }: AdvanceFilterNewProps) => {
  const methods = useFormContext();
  const watchField = !methods.watch('excludeSocialOnlyProfiles');
  const disabledInfoMessage =
    "Only available when the 'Exclude Social Only Profile' checkbox is selected";
  const { handleSubmit, reset } = methods;
  return (
    <FormProvider {...methods}>
      <Paper sx={{ mt: 2 }}>
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <AdvancedFiltersHeader />
          <Divider />
          <List>
            <ExperienceRangeFilter />
            <MultiMatchFilter
              name="skills.values"
              title="Skills"
              placeholder="Skills"
              matchTypeFieldName="skills.matchType"
              startIcon={<LuBookOpenCheck color={WsColors.TertiaryShades800} />}
            />
            <MultiMatchFilter
              name="jobTitle.values"
              title="Job Title"
              placeholder="Job Titles"
              matchTypeFieldName="jobTitle.matchType"
              startIcon={<TbBriefcase color={WsColors.TertiaryShades800} />}
            />
            <MultiMatchFilter
              name="companies.values"
              title="Companies"
              placeholder="Companies"
              matchTypeFieldName="companies.matchType"
              startIcon={<LuBuilding color={WsColors.TertiaryShades800} />}
            />
            <CheckboxFilter
              name="excludeSocialOnlyProfiles"
              title="Profile Source"
              label="Exclude Social Only Profiles"
              startIcon={<LuGlobe color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="contactMethod"
              options={{
                email: 'Email',
                phone: 'Phone',
              }}
              title="Contact Method"
              startIcon={<LuMail color={WsColors.TertiaryShades800} />}
            />
            <RadioListFilter
              name="likelyToMove"
              row={false}
              options={likelyToMoveOptions}
              title="Likely To Switch"
              startIcon={<LuRefreshCw color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="education"
              options={educationCheckBoxOptions}
              title="Education"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuGraduationCap color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="languages"
              options={languagesCheckBoxOptions}
              title="Languages"
              startIcon={<LuLanguages color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="additionalFilters"
              options={additionalFiltersCheckBoxOptions}
              title="Additional Filters"
              startIcon={<LuFilter color={WsColors.TertiaryShades800} />}
            />
            <TextInputFilter
              name="resumeLastUpdated"
              placeholder="# of Days"
              title="Resume Last Updated"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuCalendar color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="employmentType"
              options={employmentTypeCheckBoxOptions}
              title="Employment Type"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuFileClock color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="authorizedCountries"
              options={authorizedCountriesCheckBoxOptions}
              title="Authorized Countries"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuGlobe color={WsColors.TertiaryShades800} />}
            />
            <CheckboxListFilter
              name="workAuthorization"
              options={workAuthorizationCheckBoxOptions}
              title="Work Authorization"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuPlane color={WsColors.TertiaryShades800} />}
            />
            <SalaryRangeFilter
              title="Compensation"
              selectArray={compensationOptions}
              selectArray1={compensationTimeOptions}
              disabled={watchField}
              infoText={disabledInfoMessage}
              showAccordionIcon={true}
            />
            <CheckboxFilter
              name="hasSecurityClearance"
              title="Has Security Clearance"
              label="Only with security clearance"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuStamp color={WsColors.TertiaryShades800} />}
            />
            <CheckboxFilter
              name="willingToRelocate"
              title="Willing to Relocate"
              label="Willing To Relocate Anywhere"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuMap color={WsColors.TertiaryShades800} />}
            />
            <CheckboxFilter
              name="willingToRelocatePreferredLocationsOnly"
              title="Willing To Relocate Preferred Locations Only"
              label="Willing To Relocate Preferred Locations Only"
              startIcon={<LuMap color={WsColors.TertiaryShades800} />}
            />
            <CheckboxFilter
              name="willingToRelocateIncludeLocals"
              title="Willing To Relocate Include Locals"
              label="Willing To Relocate Include Locals"
              startIcon={<LuMap color={WsColors.TertiaryShades800} />}
            />
            <RadioListFilter
              name="travelPreference"
              row={false}
              options={travelPreferenceOptions}
              title="Travel Preference"
              disabled={watchField}
              infoText={disabledInfoMessage}
              startIcon={<LuNavigation color={WsColors.TertiaryShades800} />}
            />
          </List>
        </Box>
      </Paper>
    </FormProvider>
  );
};

export default AdvanceFilterNew;
